import { useLingui } from '@lingui/react'
import { useMutation } from '@tanstack/react-query'
import { Model, resolveProductDescription } from 'algu-domain'
import CameraControls from 'camera-controls'
import { useCallback } from 'react'
import { PerspectiveCamera, Sphere, Vector3, WebGLRenderer } from 'three'
import { v4 } from 'uuid'

import { Legacy } from '@salsita-ai/platform-project-sdk/configurator'
import { useLogger } from '@salsita-ai/platform-project-sdk/react'

import { configuratorInterface } from '../systems/configurator-interface.js'
import { useWebComponentLanguage, useWebComponentProperties } from '../systems/web-component-properties'

export const FinalizeButton = () => {
  const apiClient = configuratorInterface.useAuthorizedClient()
  const committedState = configuratorInterface.useCommittedState()
  const { imageRenderingSystem } = configuratorInterface.useConfigurator()
  const { configurationId, domain } = configuratorInterface.useDomain()
  const { configurationPersistSystem } = configuratorInterface.useDomainApplication()

  const { session, addToCartUrl, cartUrl } = useWebComponentProperties()
  const language = useWebComponentLanguage()
  const logger = useLogger()
  const { i18n } = useLingui()

  const generateImage = useCallback(
    async (finalizationUuid: string, tableSize: number, topType: Model.Top['type'], debug = false) =>
      imageRenderingSystem.generateImages(
        finalizationUuid,
        [
          {
            name: 'thumbnail',
            width: 256,
            height: 256,
            getCamera: async () => {
              const camera = new PerspectiveCamera(35, 1, 1000, 500000)

              const controls = new CameraControls(camera)

              const fitCenter =
                topType === Model.TopCircle.type ? new Vector3(0, 0, 0) : new Vector3(tableSize * 0.1, 0, 0)
              const fitRadius =
                topType === Model.TopCircle.type || topType === Model.TopRectangle.type
                  ? tableSize * 0.6
                  : tableSize * 0.5
              await controls.fitToSphere(new Sphere(fitCenter, fitRadius), false)
              await controls.rotateTo(-0.6, 1.2, false)

              // To mock the "animations"
              controls.update(1000000)

              return camera
            },
            getRenderer: async canvas => {
              const renderer = new WebGLRenderer({
                antialias: true,
                canvas,
                preserveDrawingBuffer: true,
              })
              renderer.toneMapping = 4
              renderer.toneMappingExposure = 5
              return renderer
            },
            fileFormat: 'JPEG',
          },
        ],
        debug
      ),
    [imageRenderingSystem]
  )

  const saveAndFinalize = useCallback(async () => {
    const $table = domain.getRoot$Entity(committedState)
    logger.info(`Finalizing configuration.\nProduct description: ${resolveProductDescription($table, i18n)}`)

    const finalizationUuid = v4()
    const tableSize = Model.TopCircle.is($table.$top) ? $table.$top.diameter : $table.$top.length

    if (!session || !addToCartUrl) {
      await generateImage(finalizationUuid, tableSize, $table.$top.type, true)
      throw new Error('No attributes found in encapsulation info, it will not be possible to add to cart.')
    }

    await Promise.all([configurationPersistSystem.save(), generateImage(finalizationUuid, tableSize, $table.$top.type)])

    await apiClient.finalize(
      configurationId,
      finalizationUuid,
      {
        session,
        addToCartUrl,
      },
      language
    )

    await apiClient.waitForFinalizationToComplete(configurationId, finalizationUuid)
    if (cartUrl) {
      window.location.href = cartUrl
    }
  }, [
    addToCartUrl,
    apiClient,
    cartUrl,
    committedState,
    configurationId,
    configurationPersistSystem,
    domain,
    generateImage,
    i18n,
    language,
    logger,
    session,
  ])

  const { mutate, isPending } = useMutation({
    mutationFn: saveAndFinalize,
    onError: error => {
      logger.error('Finalizing configuration failed:', error)
    },
  })

  return <Legacy.ResponsiveFinalizeButton onClick={mutate} disabled={isPending} />
}
